/**
 * Typed fetch wrapper around admin-api.
 *
 * Vite proxies `/v1` + `/health` to the backend, so we're same-
 * origin from the browser's perspective — the session cookie is
 * sent automatically as long as we use `credentials: "include"`.
 * Every helper funnels through {@link request}.
 */

import { firmId } from "./firm";
import { renew } from "./noctantLogin";
import type {
  AdminRole,
  AuditEventPage,
  CompanyFeature,
  CompanyFeatures,
  CreateFirmRequest,
  CreatedFirm,
  DeploymentUnitStatus,
  EntitleableFeed,
  EntitleableFeeds,
  EnvironmentHealthCheck,
  EnvironmentHealthReport,
  EnvironmentHealthStatus,
  FirmRiskTerms,
  MeView,
  Organization,
  Organizations,
  StartedSession,
} from "../../../../../packages/admin-api-typescript-types/src/types.gen";
import type {
  AuditActorKind,
  AuditEventListCursor,
  AuditEventView,
  AuditEventsResponse,
  ChallengeListCursor,
  ChallengeListItem,
  ChallengeRequestKind,
  ChallengeView,
  ChallengesResponse,
  MemberListCursor,
  MemberRole,
  MemberView,
  MembersResponse,
  OrgChallengeSettings,
  ReinstatementClock,
  OperatorListItem,
  OperatorDetail,
  OperatorsResponse,
  OperatorEnvironment,
  OperatorRateLimitTier,
  OperatorApiPresetKey,
  CreateOperatorRequest,
  OperatorApiScopeOption,
  OperatorApiScopeOptionsResponse,
  WebhookEventTypeOption,
  WebhookEventTypeOptionsResponse,
  OperatorApiPresetView,
  OperatorApiPresetOptionsResponse,
  AppliedOperatorApiPreset,
  OperatorApiReadinessFinding,
  WebhookDeliveryStatus,
  OperatorApiLastWebhookDelivery,
  OperatorApiLastWebhookReplay,
  OperatorApiWebhookActivity,
  OperatorApiLastSmokeProof,
  OperatorApiLastOnboardingSignoff,
  RecordSmokeProofRequest,
  RecordOnboardingSignoffRequest,
  OperatorApiReadinessReport,
  ProvisionedOperatorCredentials,
  RotatedOperatorApiKey,
  RotatedSigningSecret,
  ResumedWebhookDelivery,
  WebhookUrlSetting,
  BrandLogoChange,
  BrandLogoSetting,
  AllowedOriginsSetting,
  RateLimitTierSetting,
  OperatorApiScopesSetting,
  WebhookSubscriptionSetting,
  WebhookSubscriptionView,
  CreateTemplateRequest,
  PublishTemplateRequest,
  PublishedRevisionView,
  TemplateListCursor,
  TemplateListResponse,
  TemplateResponse,
} from "../../../../../packages/operator-api-typescript-sdk/src/types.gen";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail?: string,
  ) {
    super(detail ?? code);
    this.name = "ApiError";
  }

  /** Convenience helpers for the few statuses the UI reacts to. */
  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
}

/** What a failed call says: the API's own sentence (an {@link ApiError}'s
 *  message is its `detail`, else its code), or the thrown error's. */
export function failureSentence(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

type ErrorBody = { error: string; detail?: string };

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const finalInit: RequestInit = {
    credentials: "include",
    ...rest,
    headers: {
      Accept: "application/json",
      ...(json === undefined ? {} : { "Content-Type": "application/json" }),
      ...(headers ?? {}),
    },
    ...(json === undefined ? {} : { body: JSON.stringify(json) }),
  };

  let resp = await fetch(path, finalInit);
  if (
    resp.status === 401 &&
    (path.startsWith("/v1/") || path.startsWith("/firm/")) &&
    !path.startsWith("/v1/auth/")
  ) {
    // A failed renewal leaves the 401 the session gate uses to show the form.
    const renewed = await renew().then(() => true, () => false);
    if (renewed) resp = await fetch(path, finalInit);
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const parsed = text ? safeParse(text) : undefined;

  if (!resp.ok) {
    const err = parsed as ErrorBody | undefined;
    throw new ApiError(resp.status, err?.error ?? "unknown", err?.detail);
  }

  return parsed as T;
}

/**
 * A Firm API request (operator-api, served on this origin under `/firm`) with
 * the staff session cookie. `orgId` names the firm a platform staff member
 * acts in; a firm's staff act in their own whatever it says.
 */
function firmRequest<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
  orgId: string | null = firmId(),
): Promise<T> {
  return request<T>(`/firm${path}`, {
    ...init,
    headers: {
      ...(orgId ? { "X-Org-Id": orgId } : {}),
      ...(init.headers ?? {}),
    },
  });
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ---------------------------------------------------------------
// The console's shapes, generated from its contract
// (packages/admin-api-typescript-types, from admin-api's door): never
// restated here.
// ---------------------------------------------------------------

export type {
  AdminRole,
  AuditEventPage,
  CompanyFeature,
  CompanyFeatures,
  CreateFirmRequest,
  CreatedFirm,
  DeploymentUnitStatus,
  EntitleableFeed,
  EnvironmentHealthCheck,
  EnvironmentHealthReport,
  EnvironmentHealthStatus,
  FirmRiskTerms,
  MeView,
  Organization,
};

// ---------------------------------------------------------------
// The Firm API's shapes, generated from its contract
// (packages/operator-api-typescript-sdk): never restated here.
// ---------------------------------------------------------------

export type {
  AuditActorKind,
  AuditEventListCursor,
  AuditEventView,
  AuditEventsResponse,
  ChallengeListCursor,
  ChallengeListItem,
  ChallengeRequestKind,
  ChallengeView,
  ChallengesResponse,
  MemberListCursor,
  MemberRole,
  MemberView,
  MembersResponse,
  OrgChallengeSettings,
  ReinstatementClock,
  OperatorListItem,
  OperatorDetail,
  OperatorsResponse,
  OperatorEnvironment,
  OperatorRateLimitTier,
  OperatorApiPresetKey,
  CreateOperatorRequest,
  OperatorApiScopeOption,
  OperatorApiScopeOptionsResponse,
  WebhookEventTypeOption,
  WebhookEventTypeOptionsResponse,
  OperatorApiPresetView,
  OperatorApiPresetOptionsResponse,
  AppliedOperatorApiPreset,
  OperatorApiReadinessFinding,
  WebhookDeliveryStatus,
  OperatorApiLastWebhookDelivery,
  OperatorApiLastWebhookReplay,
  OperatorApiWebhookActivity,
  OperatorApiLastSmokeProof,
  OperatorApiLastOnboardingSignoff,
  RecordSmokeProofRequest,
  RecordOnboardingSignoffRequest,
  OperatorApiReadinessReport,
  ProvisionedOperatorCredentials,
  RotatedOperatorApiKey,
  RotatedSigningSecret,
  ResumedWebhookDelivery,
  WebhookUrlSetting,
  BrandLogoChange,
  BrandLogoSetting,
  AllowedOriginsSetting,
  RateLimitTierSetting,
  OperatorApiScopesSetting,
  WebhookSubscriptionSetting,
  WebhookSubscriptionView,
  TemplateListCursor,
};

/** One Operator API scope token, as the contract enumerates them. */
export type OperatorApiScope = OperatorApiScopeOption["value"];
/** One webhook event type token (the vocabulary is served, not enumerated). */
export type WebhookEventType = string;

// ---------------------------------------------------------------
// Endpoint helpers — one function per admin-api route.
// ---------------------------------------------------------------

export const api = {
  // Auth
  signIn: (token: string) =>
    request<StartedSession>("/v1/auth/sign-in", {
      method: "POST",
      json: { token },
    }),

  logout: () => request<void>("/v1/auth/logout", { method: "POST" }),

  me: () => request<MeView>("/v1/me"),

  // Firms, for platform staff choosing the firm they act in
  listOrganizations: () =>
    request<Organizations>("/v1/organizations"),
  // A company's feature ticks (platform staff): read, and changed from the
  // ticks read, with the reason
  getCompanyFeatures: (orgId: string) =>
    request<CompanyFeatures>(`/v1/organizations/${orgId}/features`),
  setCompanyFeatures: (
    orgId: string,
    expected: CompanyFeature[],
    features: CompanyFeature[],
    reason: string,
  ) =>
    request<CompanyFeatures>(`/v1/organizations/${orgId}/features`, {
      method: "PUT",
      json: { expected, features, reason },
    }),
  // Making a firm (platform staff): the feeds it may trade on, and the firm
  listFeeds: () => request<EntitleableFeeds>("/v1/feeds"),
  createFirm: (firm: CreateFirmRequest) =>
    request<CreatedFirm>("/v1/firms", { method: "POST", json: firm }),

  // The firm's members (Firm API)
  listMembers: (q: { limit?: number; email?: string } & Partial<MemberListCursor> = {}) =>
    firmRequest<MembersResponse>(
      "/v1/members" + queryString(q as Record<string, unknown>),
    ),
  getMember: (globalUserId: string) =>
    firmRequest<{ member: MemberView }>(`/v1/members/${globalUserId}`),

  // The firm's challenges (Firm API)
  listChallenges: (
    q: {
      limit?: number;
      status?: string;
      global_user_id?: string;
    } & Partial<ChallengeListCursor> = {},
  ) =>
    firmRequest<ChallengesResponse>(
      "/v1/challenges" + queryString(q as Record<string, unknown>),
    ),
  getChallenge: (instanceId: string) =>
    firmRequest<ChallengeView>(`/v1/challenges/${instanceId}`),
  resetChallenge: (instanceId: string, idempotencyKey: string) =>
    firmRequest<ChallengeView>(`/v1/challenges/${instanceId}/reset`, {
      method: "POST",
      json: { idempotency_key: idempotencyKey },
    }),

  // The firm's challenge templates (Firm API): the organisation's own, and
  // the platform presets it may copy, newest first.
  listTemplates: (q: { limit?: number } & Partial<TemplateListCursor> = {}) =>
    firmRequest<TemplateListResponse>("/v1/templates" + queryString(q as Record<string, unknown>)),
  /** The body that authors the template again: what a copy or an edit loads. */
  getTemplateAuthoring: (templateId: string) =>
    firmRequest<CreateTemplateRequest>(`/v1/templates/${templateId}/authoring`),
  createTemplate: (request: CreateTemplateRequest) =>
    firmRequest<TemplateResponse>("/v1/templates", { method: "POST", json: request }),
  updateTemplate: (templateId: string, request: CreateTemplateRequest) =>
    firmRequest<TemplateResponse>(`/v1/templates/${templateId}`, {
      method: "PATCH",
      json: request,
    }),
  archiveTemplate: (templateId: string) =>
    firmRequest<void>(`/v1/templates/${templateId}/archive`, { method: "POST" }),
  publishTemplate: (templateId: string, request: PublishTemplateRequest) =>
    firmRequest<PublishedRevisionView>(`/v1/templates/${templateId}/publish`, {
      method: "POST",
      json: request,
    }),

  // The firm's integrations (Firm API)
  listOperatorApiScopes: () =>
    firmRequest<OperatorApiScopeOptionsResponse>("/v1/operator-api-scopes"),
  listOperatorApiPresets: () =>
    firmRequest<OperatorApiPresetOptionsResponse>("/v1/operator-api-presets"),
  listWebhookEventTypes: () =>
    firmRequest<WebhookEventTypeOptionsResponse>("/v1/webhook-event-types"),
  listOperators: () => firmRequest<OperatorsResponse>("/v1/operators"),
  createOperator: (payload: CreateOperatorRequest) =>
    firmRequest<OperatorDetail>("/v1/operators", {
      method: "POST",
      json: payload,
    }),
  getOperator: (operatorId: string) =>
    firmRequest<OperatorDetail>(`/v1/operators/${operatorId}`),
  getOperatorApiReadiness: (operatorId: string) =>
    firmRequest<OperatorApiReadinessReport>(
      `/v1/operators/${operatorId}/operator-api-readiness`,
    ),
  recordOperatorSmokeProof: (operatorId: string, proof: RecordSmokeProofRequest) =>
    firmRequest<OperatorApiLastSmokeProof>(
      `/v1/operators/${operatorId}/smoke-proofs`,
      { method: "POST", json: proof },
    ),
  recordOperatorOnboardingSignoff: (
    operatorId: string,
    signoff: RecordOnboardingSignoffRequest,
  ) =>
    firmRequest<OperatorApiLastOnboardingSignoff>(
      `/v1/operators/${operatorId}/onboarding-signoffs`,
      { method: "POST", json: signoff },
    ),
  applyOperatorApiPreset: (
    operatorId: string,
    presetKey: OperatorApiPresetKey,
  ) =>
    firmRequest<AppliedOperatorApiPreset>(
      `/v1/operators/${operatorId}/operator-api-preset`,
      { method: "PATCH", json: { preset_key: presetKey } },
    ),
  provisionCredentials: (operatorId: string) =>
    firmRequest<ProvisionedOperatorCredentials>(
      `/v1/operators/${operatorId}/provision-credentials`,
      { method: "POST" },
    ),
  rotateApiKey: (operatorId: string) =>
    firmRequest<RotatedOperatorApiKey>(
      `/v1/operators/${operatorId}/rotate-api-key`,
      { method: "POST" },
    ),
  rotateSigningSecret: (operatorId: string) =>
    firmRequest<RotatedSigningSecret>(
      `/v1/operators/${operatorId}/rotate-signing-secret`,
      { method: "POST" },
    ),
  resumeWebhookDelivery: (operatorId: string, rotationId: string) =>
    firmRequest<ResumedWebhookDelivery>(
      `/v1/operators/${operatorId}/resume-webhook-delivery`,
      { method: "POST", json: { rotation_id: rotationId } },
    ),
  updateWebhookUrl: (operatorId: string, webhookUrl: string | null) =>
    firmRequest<WebhookUrlSetting>(`/v1/operators/${operatorId}/webhook-url`, {
      method: "PATCH",
      json: { webhook_url: webhookUrl },
    }),
  updateAllowedOrigins: (operatorId: string, allowedOrigins: string[]) =>
    firmRequest<AllowedOriginsSetting>(
      `/v1/operators/${operatorId}/allowed-origins`,
      { method: "PATCH", json: { allowed_origins: allowedOrigins } },
    ),
  updateRateLimitTier: (
    operatorId: string,
    rateLimitTier: OperatorRateLimitTier,
  ) =>
    firmRequest<RateLimitTierSetting>(
      `/v1/operators/${operatorId}/rate-limit-tier`,
      { method: "PATCH", json: { rate_limit_tier: rateLimitTier } },
    ),
  updateOperatorApiScopes: (
    operatorId: string,
    operatorApiScopes: OperatorApiScope[],
  ) =>
    firmRequest<OperatorApiScopesSetting>(
      `/v1/operators/${operatorId}/operator-api-scopes`,
      { method: "PATCH", json: { operator_api_scopes: operatorApiScopes } },
    ),
  updateWebhookSubscription: (
    operatorId: string,
    subscription: WebhookSubscriptionSetting,
  ) =>
    firmRequest<WebhookSubscriptionView>(
      `/v1/operators/${operatorId}/webhook-event-types`,
      { method: "PATCH", json: subscription },
    ),

  // A firm's logo, the one its traders see (Firm API): `orgId` names the firm,
  // or null for the staff member's own.
  brandLogo: (orgId: string | null) =>
    firmRequest<BrandLogoSetting>("/v1/brand-logo", {}, orgId),
  setBrandLogo: (orgId: string | null, change: BrandLogoChange) =>
    firmRequest<BrandLogoSetting>(
      "/v1/brand-logo",
      { method: "PATCH", json: change },
      orgId,
    ),

  // A firm's challenge settings (Firm API)
  getChallengeSettings: (orgId: string) =>
    firmRequest<OrgChallengeSettings>("/v1/challenge-settings", {}, orgId),
  changeChallengeSettings: (
    orgId: string,
    expected: OrgChallengeSettings,
    settings: OrgChallengeSettings,
    reason: string,
  ) =>
    firmRequest<OrgChallengeSettings>(
      "/v1/challenge-settings",
      { method: "POST", json: { expected, settings, reason } },
      orgId,
    ),

  // The firm's audit trail (Firm API)
  listAuditEvents: (
    q: {
      limit?: number;
      actor_type?: AuditActorKind;
      action?: string;
      target_type?: string;
    } & Partial<AuditEventListCursor> = {},
  ) =>
    firmRequest<AuditEventsResponse>(
      "/v1/audit-events" + queryString(q as Record<string, unknown>),
    ),
  // The platform's own events (no firm), for platform staff
  listPlatformAuditLog: (
    q: {
      limit?: number;
      offset?: number;
      actor_type?: string;
      action?: string;
      target_type?: string;
    } = {},
  ) =>
    request<AuditEventPage>(
      "/v1/audit-log" + queryString(q as Record<string, unknown>),
    ),

  // Environment health
  getEnvironmentHealth: () =>
    request<EnvironmentHealthReport>("/v1/environment-health"),
};

function queryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (entries.length === 0) return "";
  const q = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();
  return `?${q}`;
}
